import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Empty, Skeleton, Spin, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import useVideoResources from '../Hooks/useVideoResources';
import { getResourcesQueryKey } from '../Hooks/useResourcesByType';
import { GALLERY_BATCH_SIZE } from '@/utilities/helpers/galleryUtils';
import { generateVideoThumbnailWithTimeout } from '@/utilities/helpers/videoThumbnailUtils';
import GalleryPreviewModal from './GalleryPreviewModal';
import './Gallery.less';

const VIDEO_THUMBNAIL_TIMEOUT_MS = 12000;

/**
 * GalleryVideos Component
 * Displays a grid of video thumbnails with metadata
 * Uses React Query for fetching videos, Canvas API for thumbnail generation
 */
const GalleryVideos = ({ hubRecord }) => {
  const trans = useSelector(translator);
  const shareResourcesApi = useSelector((state) => state.apis.shareResourcesApi);
  const queryClient = useQueryClient();

  // State management for thumbnails
  const [thumbnails, setThumbnails] = useState({});
  const [generatingThumbnails, setGeneratingThumbnails] = useState(new Set());
  const [activeVideo, setActiveVideo] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [visibleCount, setVisibleCount] = useState(GALLERY_BATCH_SIZE);

  // Use hubRecord's useruuid (account being viewed), not logged-in user
  const accountUuid = hubRecord?.useruuid;

  // Fetch videos using React Query hook
  const {
    data: videos = [],
    isLoading,
    error,
  } = useVideoResources({
    useruuid: accountUuid,
  });

  const visibleVideos = videos.slice(0, visibleCount);
  const hasMoreVideos = visibleCount < videos.length;

  /**
   * Generate thumbnail for a video when it comes into view
   * Stores thumbnail in component state to avoid regeneration
   */
  const generateThumbnail = async (video) => {
    if (thumbnails[video.resourceuuid] || generatingThumbnails.has(video.resourceuuid)) {
      return;
    }

    setGeneratingThumbnails((prev) => new Set([...prev, video.resourceuuid]));

    try {
      // Retry with an earlier frame before marking the thumbnail as failed.
      let thumbnail = await generateVideoThumbnailWithTimeout(
        video.url,
        VIDEO_THUMBNAIL_TIMEOUT_MS,
        1
      );
      if (!thumbnail) {
        thumbnail = await generateVideoThumbnailWithTimeout(
          video.url,
          VIDEO_THUMBNAIL_TIMEOUT_MS,
          0.2
        );
      }

      setThumbnails((prev) => ({
        ...prev,
        [video.resourceuuid]: thumbnail,
      }));
    } catch (err) {
      console.warn(`Failed to generate thumbnail for ${video.title}:`, err);
      // Do not overwrite an already generated thumbnail on late failures.
      setThumbnails((prev) => ({
        ...prev,
        [video.resourceuuid]: prev[video.resourceuuid] || null,
      }));
    } finally {
      setGeneratingThumbnails((prev) => {
        const next = new Set(prev);
        next.delete(video.resourceuuid);
        return next;
      });
    }
  };

  /**
   * Reset thumbnails and log when videos change (user switch)
   */
  useEffect(() => {
    setThumbnails({});
    setGeneratingThumbnails(new Set());
    setVisibleCount(GALLERY_BATCH_SIZE);
  }, [accountUuid]);

  useEffect(() => {
    if (!hasMoreVideos) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          setVisibleCount((previousCount) =>
            Math.min(previousCount + GALLERY_BATCH_SIZE, videos.length)
          );
        });
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    const sentinelElement = document.getElementById('gallery-videos-scroll-sentinel');
    if (sentinelElement) {
      observer.observe(sentinelElement);
    }

    return () => observer.disconnect();
  }, [hasMoreVideos, videos.length]);

  useEffect(() => {
    if (!videos || videos.length === 0) {
      setThumbnails({});
      setGeneratingThumbnails(new Set());
      return;
    }

    const validResourceIds = new Set(videos.map((video) => video.resourceuuid));

    // Keep already generated thumbnails for unchanged videos to avoid flicker/retries.
    setThumbnails((prev) => {
      const next = {};
      Object.entries(prev).forEach(([resourceuuid, thumb]) => {
        if (validResourceIds.has(resourceuuid)) {
          next[resourceuuid] = thumb;
        }
      });
      return next;
    });

    setGeneratingThumbnails((prev) => {
      const next = new Set();
      prev.forEach((resourceuuid) => {
        if (validResourceIds.has(resourceuuid)) {
          next.add(resourceuuid);
        }
      });
      return next;
    });
  }, [videos, accountUuid]);

  /**
   * Generate thumbnails for videos on initial load
   * Uses a queue approach similar to mobile app
   */
  useEffect(() => {
    if (visibleVideos && visibleVideos.length > 0) {
      // Generate first few thumbnails immediately
      visibleVideos.slice(0, 3).forEach((video) => {
        generateThumbnail(video);
      });

      // Generate remaining thumbnails with slight delay
      const timer = setTimeout(() => {
        visibleVideos.slice(3).forEach((video) => {
          generateThumbnail(video);
        });
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleVideos]);

  const openVideoPlayer = (video) => {
    const videoUrl = video?.url || video?.downloadurl;
    if (!videoUrl) {
      return;
    }

    setActiveVideo({
      ...video,
      playbackUrl: videoUrl,
    });
  };

  const closeVideoPlayer = () => {
    if (isRemoving) {
      return;
    }
    setActiveVideo(null);
  };

  const unshareVideoResource = async (resourceuuid) => {
    return new Promise((resolve, reject) => {
      shareResourcesApi.shareResources(
        accountUuid,
        'unshare',
        'videos',
        { resourceuuid: [resourceuuid] },
        apiCallback({
          translator: trans,
          successCallback: resolve,
          failCallback: reject,
          errorCallback: reject,
        })
      );
    });
  };

  const handleUnshareVideo = async (video) => {
    if (!video?.resourceuuid || !shareResourcesApi || !accountUuid || isRemoving) {
      return;
    }

    setIsRemoving(true);

    try {
      await unshareVideoResource(video.resourceuuid);
      await queryClient.invalidateQueries({
        queryKey: getResourcesQueryKey({
          resourceType: 'videos',
          useruuid: accountUuid,
          shared: false,
        }),
      });
      setActiveVideo(null);
      message.success(trans('GALLERY_PREVIEW_UNSHARE_SUCCESS'));
    } catch (error) {
      console.error('Failed to unshare video resource:', error);
      message.error(trans('GALLERY_PREVIEW_UNSHARE_ERROR'));
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="gallery-videos-container">
        <div className="gallery-videos-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="gallery-video-item">
              <Skeleton.Button active block className="gallery-skeleton-video-thumb" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !videos || videos.length === 0) {
    return (
      <div className="gallery-videos-container">
        <Empty description={trans('NODATAFOUND')} />
      </div>
    );
  }

  return (
    <div className="gallery-videos-container">
      <div className="gallery-videos-grid">
        {visibleVideos.map((video) => {
          const hasThumbnail = thumbnails[video.resourceuuid] !== undefined;
          const isGenerating = generatingThumbnails.has(video.resourceuuid);
          const thumbnail = thumbnails[video.resourceuuid];
          // REVIEW: Debug logging includes full video payload (can contain signed URLs); remove or guard by environment before merge.
          return (
            <div
              key={video.resourceuuid}
              className="gallery-video-item"
              role="button"
              tabIndex={0}
              onClick={() => openVideoPlayer(video)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openVideoPlayer(video);
                }
              }}
            >
              <div className="gallery-video-thumbnail-wrapper">
                {hasThumbnail && thumbnail ? (
                  // Successfully generated thumbnail
                  <img src={thumbnail} alt={video.title} className="gallery-video-thumbnail" />
                ) : isGenerating ? (
                  // Still generating thumbnail
                  <div className="gallery-thumbnail-loading">
                    <Spin size="small" />
                  </div>
                ) : hasThumbnail && !thumbnail ? (
                  // Thumbnail generation failed or CORS blocked - show fallback
                  <div className="gallery-thumbnail-placeholder gallery-thumbnail-fallback" />
                ) : (
                  // Initial state
                  <div className="gallery-thumbnail-placeholder" />
                )}

                {/* Play button overlay */}
                <div className="gallery-video-play-overlay">
                  <div className="gallery-play-icon">
                    <span>▶</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {hasMoreVideos && <div id="gallery-videos-scroll-sentinel" style={{ height: 1 }} />}

      <GalleryPreviewModal
        open={!!activeVideo}
        onClose={closeVideoPlayer}
        resource={activeVideo}
        resourceType="videos"
        onUnshare={handleUnshareVideo}
        isRemoving={isRemoving}
        unshareLabel={trans('GALLERY_PREVIEW_REMOVE_FROM_ACCOUNT_ACTION')}
      />
    </div>
  );
};

GalleryVideos.propTypes = {
  hubRecord: PropTypes.object,
};

export default GalleryVideos;
